import React, { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import config from "config";
import {
  PageLayout,
  scrollPageLayoutTo,
} from "components/molecules/pageLayout";
import { OneColumn } from "components/atoms/oneColumn";
import { Breadcrumbs } from "components/molecules/breadcrumb";
import { useDevice } from "contexts/deviceContext";
import { NoResult } from "components/atoms/noResult";
import { EventGrid } from "components/atoms/eventGrid";
import { EventCard } from "components/molecules/eventCard";
import { Pager } from "components/molecules/pager";
import { InterviewBanner } from "components/atoms/interviewBanner";

import noResultsDesktop from "../../../assets/images/noResultsDesktop.png";
import noResultsMobile from "../../../assets/images/noResultsMobile.png";
import { EventDesktopFilters } from "./EventDesktopFilters";
import { useForm } from "../../form";
import EventMobileFilters from "./EventMobileFilters";
import styles from "./PageListEvents.module.scss";
import { GroupedEventInstanceItem } from "../../types";
import {
  EventCategoryItemFragment,
  IndustryItemFragment,
} from "../../generated/graphql";
import { Routes } from "../../generated/routes";
import { wrap } from "../../lib/hoc";

const ITEM_PER_PAGE = 9;

interface Props {
  page: number;
  groupedEventInstances: GroupedEventInstanceItem[];
  totalCount: number;
  industries: Array<IndustryItemFragment>;
  eventCategories: Array<EventCategoryItemFragment>;
  initFilters: ListEventsFilterForm;
  canApplyForInt?: boolean;
}

export type ListEventsFilterForm = {
  endDate: string | null;
  startDate: string | null;
  title: string;
  area: string | null;
  eventCategories: Array<EventCategoryItemFragment>;
  industries: Array<IndustryItemFragment>;
};

const empty: ListEventsFilterForm = {
  area: null,
  endDate: null,
  startDate: null,
  title: "",
  eventCategories: [],
  industries: [],
};

const PageListEvents: React.FC<Props> = ({
  groupedEventInstances,
  totalCount,
  page,
  industries,
  eventCategories,
  initFilters,
  canApplyForInt = false,
}) => {
  const device = useDevice();

  const isMobile = device === "mobile";
  const pages = Math.ceil(totalCount / ITEM_PER_PAGE);
  const eventsBreadCrumbs = [{ label: "イベント一覧" }];
  const form = useForm<ListEventsFilterForm>({
    initValue: initFilters,
    validators: {},
  });
  const { field, formValues } = form;
  const clearFields = () => {
    // TODO: dedicated setFormValue from useForm ?
    field("area").setValue(empty.area);
    field("endDate").setValue(empty.endDate);
    field("startDate").setValue(empty.startDate);
    field("title").setValue(empty.title);
    field("eventCategories").setValue(empty.eventCategories);
    field("industries").setValue(empty.industries);
  };
  const router = useRouter();
  const [isLoadingNew, setIsLoadingNew] = useState(false);
  const makeQuery = (formValues: ListEventsFilterForm) => {
    const {
      area,
      startDate,
      endDate,
      industries,
      eventCategories,
      title,
    } = formValues;
    return {
      area: area || undefined,
      endDate: endDate || undefined,
      startDate: startDate || undefined,
      title: title.trim() !== "" ? title : undefined,
      industryIds: industries.map((i) => i.id),
      categoryIds: eventCategories.map((c) => c.id),
    };
  };
  const onSubmit = async () => {
    setIsLoadingNew(true);
    await router.push(
      Routes.url("/events/page/[page]", {
        page: 1,
        ...makeQuery(formValues),
      }),
    );
    setIsLoadingNew(false);
  };
  const goToPage = async (page: number) => {
    setIsLoadingNew(true);
    scrollPageLayoutTo({
      top: 0,
      behavior: "smooth",
    });
    await router.push(
      Routes.url("/events/page/[page]", {
        page,
        ...makeQuery(formValues),
      }),
    );
    setIsLoadingNew(false);
  };

  return (
    <PageLayout
      title="日本全国で就活イベントを開催中！en-courage限定のイベント一覧 | en-courage"
      metaDescription="en-courageが日本全国で開催する無料イベントの一覧です。キャリアの歩み方や選考対策の方法がわかる大学イベントから、厳選企業が集まるen-courage限定のイベントまで 。気になるイベントを見つけ、簡単にエントリーができます。"
    >
      <div className={styles.main}>
        <OneColumn>
          <Breadcrumbs breadcrumbs={eventsBreadCrumbs} />
          {isMobile ? (
            <EventMobileFilters
              clearFields={clearFields}
              form={form}
              onSubmit={onSubmit}
              industries={industries}
              eventCategories={eventCategories}
            />
          ) : (
            <EventDesktopFilters
              clearFields={clearFields}
              form={form}
              onSubmit={onSubmit}
              industries={industries}
              eventCategories={eventCategories}
            />
          )}
          {canApplyForInt && (
            <InterviewBanner className={styles.interviewBanner} />
          )}
          <div className={styles.list}>
            {groupedEventInstances.length === 0 ? (
              <NoResult
                iconDesktop={noResultsDesktop}
                iconMobile={noResultsMobile}
                mainText="ご指定の条件に該当する検索結果が見つかりませんでした。"
                subText="検索条件を変更して再度検索してください。"
              />
            ) : (
              <EventGrid>
                {groupedEventInstances.map((groupedEventInstance) => (
                  <Link
                    key={`${groupedEventInstance.instanceType}${
                      groupedEventInstance.instanceType ===
                      "GroupedScheduledEventDates"
                        ? groupedEventInstance.scheduledEvent
                        : groupedEventInstance.id
                    }`}
                    href={Routes.url(
                      "/events/[hash]",
                      groupedEventInstance.instanceType ===
                        "GroupedScheduledEventDates"
                        ? {
                            hash:
                              groupedEventInstance.scheduledEvent.event.hash,
                            venue:
                              groupedEventInstance
                                .selectedScheduledEventDates[0].id,
                          }
                        : {
                            hash: groupedEventInstance.event.hash,
                          },
                    )}
                  >
                    <a
                      {...{ "data-e2e": "event-list-event-card" }}
                      style={{ opacity: isLoadingNew ? 0.2 : 1 }}
                    >
                      <EventCard eventInstance={groupedEventInstance} />
                    </a>
                  </Link>
                ))}
              </EventGrid>
            )}
          </div>
          <div className={styles.pager}>
            <Pager currentPage={page} onChange={goToPage} pages={pages} />
          </div>
        </OneColumn>
      </div>
    </PageLayout>
  );
};

export default wrap(PageListEvents, async () => {
  return { status: "redirectTo", path: `${config.BAKLAVA_URL}/events` };
});
